// Mirrors backend/data.py. Used when the Flask API is unreachable so the page
// still renders something sensible during frontend-only development.

export const FALLBACK_STATS = {
  specialties: 137,
  conditions: 616,
  therapists: 73,
  verifiedAvatars: [
    'https://randomuser.me/api/portraits/women/44.jpg',
    'https://randomuser.me/api/portraits/men/32.jpg',
    'https://randomuser.me/api/portraits/women/68.jpg',
    'https://randomuser.me/api/portraits/men/75.jpg',
  ],
};

export const FALLBACK_THERAPISTS = [
  {
    id: 1,
    name: 'Dr. Ananya Rao',
    image: 'https://randomuser.me/api/portraits/women/44.jpg',
    verified: true,
    title: 'Clinical Psychologist',
    registration: 'RCI Reg. A-13429',
    tags: ['Pain Management', 'Anxiety Treatment'],
    location: 'Bengaluru, Karnataka',
    price: 7000,
    currency: 'INR',
    rating: 4.9,
    sessions: 1240,
  },
  {
    id: 2,
    name: 'Dr. Vikram Mehta',
    image: 'https://randomuser.me/api/portraits/men/32.jpg',
    verified: true,
    title: 'Consultant Psychiatrist',
    registration: 'MCI Reg. 55210',
    tags: ['Depression', 'Bipolar Disorder'],
    location: 'Mumbai, Maharashtra',
    price: 9500,
    currency: 'INR',
    rating: 4.8,
    sessions: 980,
  },
  {
    id: 3,
    name: 'Sara Fernandes',
    image: 'https://randomuser.me/api/portraits/women/68.jpg',
    verified: true,
    title: 'Counselling Psychologist',
    registration: 'BACP Accredited',
    tags: ['Trauma & PTSD', 'Grief'],
    location: 'Goa, India',
    price: 4500,
    currency: 'INR',
    rating: 4.9,
    sessions: 640,
  },
  {
    id: 4,
    name: 'Dr. Imran Sheikh',
    image: 'https://randomuser.me/api/portraits/men/75.jpg',
    verified: true,
    title: 'Child & Adolescent Therapist',
    registration: 'RCI Reg. A-20871',
    tags: ['ADHD', 'Autism Support'],
    location: 'Hyderabad, Telangana',
    price: 6000,
    currency: 'INR',
    rating: 4.7,
    sessions: 1105,
  },
  {
    id: 5,
    name: 'Dr. Kavita Nair',
    image: 'https://randomuser.me/api/portraits/women/12.jpg',
    verified: true,
    title: 'CBT Practitioner',
    registration: 'RCI Reg. A-11876',
    tags: ['OCD', 'Panic Attacks'],
    location: 'Kochi, Kerala',
    price: 5500,
    currency: 'INR',
    rating: 5.0,
    sessions: 430,
  },
  {
    id: 6,
    name: 'Arjun Kapoor',
    image: 'https://randomuser.me/api/portraits/men/54.jpg',
    verified: false,
    title: 'Couples & Family Therapist',
    registration: 'MFT Licensed',
    tags: ['Relationships', 'Family Conflict'],
    location: 'New Delhi, India',
    price: 8000,
    currency: 'INR',
    rating: 4.6,
    sessions: 720,
  },
  {
    id: 7,
    name: 'Dr. Meera Iyer',
    image: 'https://randomuser.me/api/portraits/women/90.jpg',
    verified: true,
    title: 'Health Psychologist',
    registration: 'RCI Reg. A-30012',
    tags: ['Chronic Illness', 'Burnout'],
    location: 'Chennai, Tamil Nadu',
    price: 6500,
    currency: 'INR',
    rating: 4.8,
    sessions: 512,
  },
  {
    id: 8,
    name: 'Rohit Deshmukh',
    image: 'https://randomuser.me/api/portraits/men/22.jpg',
    verified: true,
    title: 'Addiction Counsellor',
    registration: 'NACT Certified',
    tags: ['Addiction Recovery', 'Anger Management'],
    location: 'Pune, Maharashtra',
    price: 4000,
    currency: 'INR',
    rating: 4.7,
    sessions: 865,
  },
];

// `symptoms` mirrors backend/data.py. Written as "what this tends to look
// like", never as diagnostic criteria — the copy above the grid is explicit
// that no diagnosis is needed to start.
export const FALLBACK_CATEGORIES = [
  {
    id: 1,
    name: 'Anxiety Treatment',
    therapists: 47,
    symptoms:
      'Racing thoughts you can’t switch off, a tight chest or churning stomach, and avoiding things you used to handle without a second thought. Often worst at night, or before anything you can’t control.',
  },
  {
    id: 2,
    name: 'Depression',
    therapists: 52,
    symptoms:
      'Feeling flat or heavy most days, losing interest in things that used to matter, and finding ordinary tasks exhausting. Sleep and appetite often shift in either direction.',
  },
  {
    id: 3,
    name: 'Trauma & PTSD',
    therapists: 31,
    symptoms:
      'Intrusive memories or nightmares, and moments where you feel suddenly back in something that has already happened. Being on edge and easily startled, or numb and oddly detached.',
  },
  {
    id: 4,
    name: 'Relationships',
    therapists: 38,
    symptoms:
      'The same argument on repeat, feeling unheard, or drifting apart without being able to say when it started. Also covers family strain and finding it hard to trust after being hurt.',
  },
  {
    id: 5,
    name: 'ADHD',
    therapists: 24,
    symptoms:
      'Starting far more than you finish, losing track of time, and a mind that runs ahead of the room. Often alongside restlessness, impulsive decisions, or exhaustion from holding it all together.',
  },
  {
    id: 6,
    name: 'Grief & Loss',
    therapists: 19,
    symptoms:
      'Waves that arrive without warning, months or even years on. Trouble concentrating, guilt about carrying on, or a sense that you ought to be over it by now.',
  },
  {
    id: 7,
    name: 'Burnout & Stress',
    therapists: 44,
    symptoms:
      'Running on empty, feeling cynical about work you used to care about, and getting less done however long you sit there. Usually shows up first as broken sleep and a very short fuse.',
  },
  {
    id: 8,
    name: 'Eating Disorders',
    therapists: 16,
    symptoms:
      'Food, weight or your body taking up more headspace than anything else. Strict rules around eating, secrecy about it, or feeling out of control and then trying to make up for it.',
  },
  {
    id: 9,
    name: 'OCD',
    therapists: 21,
    symptoms:
      'Unwanted thoughts that will not leave, and checking, washing, counting or repeating to make the discomfort stop. Relief that lasts minutes before the cycle starts again.',
  },
  {
    id: 10,
    name: 'Addiction Recovery',
    therapists: 27,
    symptoms:
      'Needing more for the same effect, meaning to cut down and not managing it, and arranging the day around it. Covers alcohol and drugs as well as gambling, gaming and other compulsions.',
  },
  {
    id: 11,
    name: 'Sleep Problems',
    therapists: 22,
    symptoms:
      'Lying awake for hours, waking at three and staying awake, or sleeping plenty and still feeling wrecked. Usually tangled up with worry, shift patterns or pain.',
  },
  {
    id: 12,
    name: 'Self-Esteem',
    therapists: 35,
    symptoms:
      'A harsh running commentary in your own head, trouble taking a compliment, and saying yes when you mean no. Often looks like perfectionism, or making yourself smaller to keep the peace.',
  },
];

export const FALLBACK_FAQS = [
  {
    id: 1,
    question: 'What is MindlyFriend?',
    answer:
      'MindlyFriend is a directory that connects you with independently verified therapists, psychologists and psychiatrists. We check every clinician’s registration and qualifications before they appear on the platform, so the shortlist you see is one you can trust.',
  },
  {
    id: 2,
    question: 'How do I book my first session?',
    answer:
      'Search by condition, availability or who the session is for, open a therapist’s profile, and pick a slot that suits you. You’ll get a confirmation email with a secure video link or clinic address within a few minutes.',
  },
  {
    id: 3,
    question: 'Are the therapists on MindlyFriend verified?',
    answer:
      'Yes. Every clinician with a blue Verified badge has had their licence, indemnity insurance and professional body membership checked by our clinical team, and we re-verify annually.',
  },
  {
    id: 4,
    question: 'How much does therapy cost?',
    answer:
      'Session fees are set by each therapist and shown up front on their card and profile, typically between ₹3,000 and ₹10,000 per session. Many clinicians offer sliding-scale or concession rates — look for the “Flexible pricing” note on their profile.',
  },
  {
    id: 5,
    question: 'Is my information kept private?',
    answer:
      'Always. Everything you share with MindlyFriend is encrypted in transit and at rest, and we never sell your data. Your therapist only sees what you choose to share when you book.',
  },
  {
    id: 6,
    question: 'Can I switch therapists if it isn’t the right fit?',
    answer:
      'Absolutely, and it’s more common than you’d think. Fit matters more than credentials for outcomes, so you can rebook with someone new at any time — no explanation needed.',
  },
];


// Wiki articles are presentational only — no endpoint was specified for them.
export const WIKI_ARTICLES = [
  {
    id: 1,
    tag: 'Understanding',
    title: 'What an anxiety attack actually feels like',
    excerpt:
      'Racing heart, tunnel vision, the sense that something is badly wrong. Here’s what’s happening in your body, and why it passes.',
    readTime: '6 min read',
    cover: 'a',
  },
  {
    id: 2,
    tag: 'Getting started',
    title: 'CBT, ACT, EMDR: which therapy does what?',
    excerpt:
      'A plain-English tour of the main modalities, what evidence backs each one, and the kinds of problems they suit best.',
    readTime: '9 min read',
    cover: 'b',
  },
  {
    id: 3,
    tag: 'Practical',
    title: 'How to prepare for your first session',
    excerpt:
      'You don’t need a script. But a few notes on what brought you here will help you use the first fifty minutes well.',
    readTime: '4 min read',
    cover: 'c',
  },
  {
    id: 4,
    tag: 'Workplace',
    title: 'Burnout isn’t just being tired',
    excerpt:
      'Exhaustion, cynicism and a drop in what you can get done. Spotting the pattern early is most of the work.',
    readTime: '7 min read',
    cover: 'd',
  },
];
